/**
 * Registration Page specific to StopTB but also built considering 
 * Nura/Matria and All medicant requirements
 * 
 * The component in support with new registration api 
 * should support creation of appointment, registration , visit creation etc
 * 
 * @description
 * @author jinshakappil 
 * @author Ashique Mohammed
 * @author Sameena

 * This component is used to gather information from the guest .
 * 
 * This components demos/explores the first use of generic forms 
 * along with custom forms . 
 * 
 * We have defined useFormUtils which accepts a configuration that is specific to the form 
 * and allows binding client side scripting to expose all the custom functions that 
 * are maintained within the useFormUtils 
 *
 */
import React, { useState, useEffect, useContext } from 'react';

import { Row, Col, Form, Select, Typography, Input, Tabs, Skeleton, message, Modal, Checkbox, Alert } from 'antd';

import {
    InputComponent,
    CountryPhoneInput,
    Location,
    useFormUtils,
    ReferenceSelect,
    useTranslation,
    GlobalContext,
    Button,
    Card,
} from 'soxo-bootstrap-core';

import AgeSelection from './components/age-selection';

import AdditionalKeyGenerate from './components/additional-key-generate';

import { GuestPreferences } from './guest-preference/guest-preferences';

import { ReadEmiratesId } from './read-emiratesid/read-emiratesid';

import { SearchOutlined } from '@ant-design/icons';

import moment from 'moment';

// Below are components created to support the registration form

import { AddressDetails } from './address-details/address-details';
import { GuestRegistrationAddressDetails } from './registration-address-details/registration-address-details';
import { PersonalDetails } from './personal-details/personal-details';
import { CampaignDetails } from './campaign-details/campaign-details';
import { OtherDetailsOfGuest } from './other-details/other-details';

// Models for use in the form
import {
    Opreg,
    Communications,
    Preferences,
    NatureList,
    Registrations,
    Referrers,
    ReferenceTypes,
    Prereg,
    Appointments,
    ListCountries,
    Customers,
    Items,
    GeneralLists,
    Maritalstatus,
} from '../../../models';

// Form Configuration specific to stoptb
// # TODO This configuration will from preferences.
import { formSettings } from './registration-form-settings/registration-form-settings';

import './new-guest-information.scss';
import { Summary } from './summary/summary';
import { ApplyCoupon } from './apply-coupon/apply-coupon';
// import { Tabs } from 'antd'

import ExistingCustomerList from './list-existing-customers';
import ExistingUser from '../../appointment/components/slot-blocking/components/exisiting-user';
import { normalizeParam } from '../../../utils/common.utils';

const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const formItemLayout = {
    layout: 'vertical',
};

// const { TabPane } = Tabs;

export default function FormContainer(props) {
    const [loading, setLoading] = useState(true);

    const [preference, setPreference] = useState({});

    useEffect(() => {
        Preferences.getRecord(props.formPreferenceId).then((result) => {
            let data = JSON.parse(result.result.scope_value);

            setPreference(data);

            setLoading(false);
        });
    }, []);

    return loading ? (
        <Card>
            <Skeleton />
        </Card>
    ) : (
        <GuestDetailsForNewPatient {...props} preference={preference} />
    );
}

/**
 *
 *
 * @param {*} props
 * @returns
 */
function GuestDetailsForNewPatient(props) {
    // Variable to control the loading state of the form
    const [loading, setLoading] = useState(true);

    const [validation, setValidation] = useState('');
    // Control the loading state of the button on submission
    const [btnloading, setBtnLoading] = useState(false);

    const [alreadyBooked, setAlreadyBooked] = useState(false);

    const [alreadyExist, setAlreadyExist] = useState(false);

    const [referrerValue, setReferrerValue] = useState(null);

    //  alert based on setting value
    const [mandatorySetting, setMandatorySetting] = useState(false);

    // for update referrer list initially
    const [mergedReferrerData, setMergedReferrerData] = useState([]);

    const [filterReferrer, setFilterReferrer] = useState(false);

    // for check mail and check phone
    const [searchBy, setSearchBy] = useState('');

    const [searchText, setSearchText] = useState('');

    const [alertOnEmail, setAlertOnEmail] = useState(false);

    const [alertOnPhone, setAlertOnPhone] = useState(false);

    //Used to show corporate offers
    const [selectCorporate, setSelectCorporate] = useState(null);

    const [corporateOptions, setCorporateOptions] = useState(null);

    const [corporateDisabled, setCorporateDisabled] = useState(false);

    const [isModalVisible, setModalVisible] = useState(false);

    const [isRemarksDisabled, setIsRemarksDisabled] = useState(false);

    //On click on view offers show offers modal
    const showOffersModal = () => setModalVisible(true);
    const closeOffersModal = () => setModalVisible(false);

    // Using the form reference supported by antd for further use
    const [form] = Form.useForm();

    const { user = {}, isMobile, settings } = useContext(GlobalContext);

    /**
     * Finds and returns the default country record from the provided options array.
     * The default country is identified by its 'lcnt_id' field matching the given initial value.
     * If no match is found, an empty object is returned.
     *
     * @param {Array} options - An array of country records to search through.
     * @returns {Object} - The country record matching the default 'lcnt_id', or an empty object if not found.
     */
    function identifyDefaultCountry(options = []) {
        // Initial value for the default country 'lcnt_id'
        let initial = parseInt(process.env.REACT_APP_DEFAULT_COUNTRY) || (initialValues && initialValues.national_id) ? initialValues.national_id : 1;
        // Object to store the matching country record
        let matching = {};

        // Iterate through each record in the options array
        options.forEach((record) => {
            // Check if the 'lcnt_id' of the current record matches the initial value
            if (record['lcnt_id'] == initial) {
                // If a match is found, assign the current record to the matching object
                matching = record;
            }
        });

        // Return the matching country record, or an empty object if no match is found
        return matching;
    }

    // UseFormUtils have specific functions as an event listner and control the configuration of the form
    const { setVisible, config, onChange } = useFormUtils(form, formSettings, props.preference);

    // Field configuration that is formatted as per requirement by the formUtils
    const { fields = {}, add_on_fields = [] } = config;

    const [initialValues, setInitialValues] = useState({
        category: 'GEN',
    });

    const [additionalValues, setAdditionalValues] = useState([]);

    const [disableCountryCode, setDisableCountryCode] = useState(false);

    const [coupon, setCoupon] = useState(null);

    const [messageApi, contextHolder] = message.useMessage();

    const [discount, setDiscount] = useState(null);

    // for store corporate_pointer
    const [corporateValue, setCorporateValue] = useState();

    const [item, setItems] = useState(null);

    const [total, setTotal] = useState(null);

    const [genderItem, setGenderItem] = useState();

    // All customers with the same email is save in existingCustomers
    const [existingCustomers, setExistingCustomers] = useState([]);

    // Visible for the modal to be opened., if any previous customer had the same mail address
    const [existingCustomerVisible, setExistingCustomerVisible] = useState(false);

    // The selcted Customer, if the mail address was used by a previous customer and that customer is selected
    const [selectedCustomer, setSelectedCustomer] = useState({});

    // If corporate is checked, we use this variable
    const [corporate, setCorporate] = useState(false);

    // Visible for alert when address is changed
    const [changeAddressAlert, setChangeAddressAlert] = useState(false);

    const [addressChangedFields, setAddressChangedFields] = useState([]);

    const [refLoading, setRefLoading] = useState(false); // Manage loading ref

    const [referrerOptions, setReferrerOptions] = useState([]); // Store referrer list

    const params = Location.search();

    const { t, i18n } = useTranslation(); // To Translate to another language

    let additionalkey;
    /*  To check the syncRegistration */
    const syncReg = settings?.SYNREG;

    /*  when the `user` object's `corporate`property is truthy. It then makes an API call to fetch customer data with a specific filter. Once
the data is fetched, it finds a matching corporate code based on the `corporate` property from the
`user` object. */
    useEffect(() => {
        if (params.id || params.edit) {
            return;
        }
        if (user?.corporate) {
            // check user have corporate ?
            const corporateCodes = Array.isArray(user.corporate) ? user.corporate : [user.corporate];

            // If the patient has one or more corporate codes assigned
            if (corporateCodes.length > 0) {
                // Fetch all active corporate codes from DB
                Customers.get({ field: 'cc_active', value: 'Y' }).then((response) => {
                    const data = response.result || response;

                    // Case: only 1 corporate code available
                    if (corporateCodes.length === 1) {
                        const corporateCode = corporateCodes[0];

                        // Try to fetch the full corporate record for that code
                        const matched = data.find((corp) => corp.cc_code === corporateCode);

                        // Pre-fill form directly because there is no ambiguity
                        form.setFieldsValue({
                            corporate: true,
                            corporate_pointer: corporateCode,
                        });

                        // Lock corporate selection because user should not change it
                        setCorporate(true);
                        setSelectCorporate(matched || { cc_code: corporateCode });
                        setCorporateDisabled(true);

                        // No dropdown options needed since selection is fixed
                        setCorporateOptions(null);
                    } else {
                        // Case: multiple possible corporate codes
                        // Filter only the active codes that match
                        const matchedCorporates = data.filter((corp) => corporateCodes.includes(corp.cc_code));

                        // Mark as corporate but do NOT set a specific code yet
                        form.setFieldsValue({
                            corporate: true,
                        });

                        setCorporate(true);

                        // User should manually choose from the dropdown
                        setCorporateDisabled(false);

                        // Populate dropdown with only the matched corporates
                        setCorporateOptions(
                            matchedCorporates.map((corp) => ({
                                label: corp.cc_desc,
                                value: corp.cc_code,
                            }))
                        );
                    }
                });
            }
        }
    }, [user, form, params.id, params.edit]);

    useEffect(async () => {
        // initially loading referrer list and stored in state
        const fetchData = async () => {
            let referrerLists = await initialReferrerLists();
            setMergedReferrerData(referrerLists);

            // If URL contains id or edit, it is an indication of a repeated case.
            // To prepopulate the columns, load guest details.
            if (params.id || params.edit) {
                // Get guest information to initialize form values from id in URL
                await loadGuestInformation(null, referrerLists);
            } else {
                if (config) {
                    setLoading(false);
                }
            }

            // If corporate is required, set the corporate checkbox as checked by default
            if (fields && fields.corporate?.required === true) {
                setCorporate(true);
            }
            // If limit_by_source is required, set the corporate checkbox as checked by default
            if (fields && fields.limit_by_source?.default_value === true) {
                setFilterReferrer(true);
            }
        };

        fetchData(); // Call the async function inside the effect
    }, [config, params.id]);

    const callBack = async (values) => {
        let fieldValue = form.getFieldValue('corporate_pointer');
        // if the corperate is selected
        if (values.target.checked) {
            // Corporate is selected
            onSelectChange(fieldValue, true);
            setCorporate(values.target.checked);
        } else {
            // if the corperate is dis selected then revert bill value
            setCorporate(values.target.checked);

            if (coupon && coupon.coupon_no) {
                let couponAmount = 0;
                let totalAmount = 0;
                let formBody = {
                    coupon_no: coupon && coupon.coupon_no ? coupon.coupon_no : null,
                    item: params.da_packagetypevalue,
                    booking_date: params.selected_date,
                    applicable_on: 'MED',
                };
                let couponDetails = await Appointments.applyCoupon(formBody);
                // Check if coupon details are returned successfully or if the initial coupon number matches the one in the coupon object
                if (couponDetails.success || initialValues.couponno === coupon.coupon_no) {
                    if (couponDetails.data.value_type === 'P') couponAmount = item.ir_rate * (couponDetails.data.coupon_value / 100);
                    else if (couponDetails.data.value_type === 'A') couponAmount = couponDetails.data.coupon_value;
                }
                totalAmount = item.ir_rate - couponAmount;
                setTotal(totalAmount);
            } else {
                setTotal(item.ir_rate);
            }
            // fields.camp_site.disabled = false; // Disable referrer field
            // fields.referral_type.disabled = false; // Disable referrer field
            // fields.limit_by_source.disabled = false
            // setValidation(fields);
        }
    };

    /**
     * On Change of values in the form
     * We have to trigger all the configuration
     *
     * @param {*} field
     * @param {*} values
     */
    const onValuesChange = (keys, values, confirmed) => {
        if (props.completeProfile) {
            // Retrieve all the field values from the form
            const fieldValues = form.getFieldValue();
            // Get all the field names (keys) from the field values

            let obj = Object.keys(fieldValues);
            // Create a copy of the current fields state to update
            const newFields = { ...fields };

            // Loop through each field name
            obj.map((ele) => {
                // Check if the field value is null
                if (fieldValues[ele] === null || fieldValues[ele] === undefined) {
                    // Update the specific field's state with a warning status and help text

                    newFields[ele] = { ...newFields[ele], validate_status: 'warning', help_text: 'Please Fill the details' };
                }
            });
            // Update the state with the new fields object containing the warnings
            setValidation(newFields);
        }

        Opreg.onValuesChange(keys, values, form, genderItem, fields, add_on_fields, onChange, initialValues, showAddressChangeAlert, confirmed);
    };

    /**
     * Callback function executed when form submission fails due to validation errors.
     *
     * This function processes the validation errors provided by the form and updates
     * the form state to reflect these errors. It is called automatically by Ant Design's
     * form handling system when the form fails to pass validation rules.
     *
     */
    const onFinishFailed = (errorInfo) => {
        // Process the error fields from errorInfo
        const errorFields = errorInfo.errorFields.reduce((acc, field) => {
            acc[field.name[0]] = field.errors[0];
            return acc;
        }, {});
        // Create a copy of the current fields state to update
        const newFields = { ...fields };
        // Set errors for each specific field
        Object.keys(errorFields).forEach((fieldName) => {
            // Update the specific field's state with an error status and help text
            newFields[fieldName] = {
                validate_status: 'error',
                help_text: errorFields[fieldName],
            };
        });
        // Update the state with the new fields object containing the error statuses and help texts

        setValidation(newFields);
    };

    /**
     * Function to alert when changing address
     * @param {*} result
     */
    const showAddressChangeAlert = (result) => {
        const values = form.getFieldsValue();
        const arr = [];
        // Get values to replaced and the values that are replacing
        const columns = [
            {
                field: 'country',
                value: 'country',
            },
            {
                field: 'place',
                value: 'city',
            },
            {
                field: 'state',
                value: 'state',
            },
            {
                field: 'address2',
                value: 'post_office',
            },
        ];

        if (params.redirect === 'registration') {
            columns.push({
                field: 'district',
                value: 'district',
            });
        }

        // Gte the previous and new values to metion in the alert
        columns.map((ele) => {
            if (result[ele['value']] !== values[ele['field']])
                arr.push({
                    field: ele['field'],
                    previousValue: values[ele['field']],
                    newvalue: result[ele['value']],
                });
        });
        if (arr.length > 0) setChangeAddressAlert(true);
        setAddressChangedFields(arr);
    };

    /**
     * This function is triggerd onblur of email address input to check if
     *  any previous customer has used the same email address
     *  Here consolidated opreg api is hit, to see if any customer in the same location had used the email address previously
     *  If yes, all those cases are listed and any account can be selected from the list
     *  It will navigate to existing-customer url, and manage the case in existing customer flow
     * @param {*} event
     */
    const onBlur = async (type) => {
        let searchText = '';
        let searchBy = '';

        const validateEmail = (email) => {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            return emailRegex.test(email);
        };

        //  Check for the field type and get the respective value
        if (type === 'email') {
            searchText = form.getFieldValue('email_address');
            searchBy = 'email';
            if (!validateEmail(searchText)) {
                return;
            }
        } else if (type === 'mobile1') {
            const phoneValue = form.getFieldValue('mobile_number');

            const mobile_number = phoneValue?.value || '';
            const countrycode = phoneValue?.code?.dialCode || '';
            // validation phone number with code
            const validationResult = validatePhoneNumber(mobile_number, countrycode);

            // Validate the mobile number format
            if (!validationResult.valid) {
                // message.warning('Please enter a valid phone number');
                return;
            }

            searchText = validationResult.value; // Ensure the value exists
            searchBy = 'mobile1';
        }

        // Update the state for searchBy and searchText
        setSearchBy(searchBy);
        setSearchText(searchText);

        if (!searchText) {
            return;
        }

        if (!params.complete_profile && (params.id || params.edit || params.redirect)) {
            return; // Prevents execution in edit or redirect cases
        }

        let formBody = {
            search_by: searchBy,
            criteria: 'anywhere',
            search_text: searchText,
            index: 'ALL',
        };
        // if (emailValue) {
        const existingCustomerArray = await Appointments.consolidatedSearch(formBody);
        setExistingCustomers(existingCustomerArray.result);

        if (existingCustomerArray.result.length > 0) {
            setExistingCustomerVisible(true);
            //only show alert if results exist
            if (type === 'email') {
                setAlertOnEmail(true);
            } else if (type === 'mobile1') {
                setAlertOnPhone(true);
            }
        } else {
            message.info(`No matching records found for this ${type === 'email' ? 'Email ID' : 'Phone Number'}`);
            setAlertOnEmail(false);
            setAlertOnPhone(false);
        }
    };

    // if (corporatePointer) {
    //     let values = {

    //         corporate_pointer: corporatePointer,
    //     };
    //     setInitialValues(values);
    // }
    /**
     * Load guest details for repeated case.
     * We get the table and id from url, get data accordingly
     */
    async function loadGuestInformation(emiratesDetails = null, mergedReferrerData = null) {
        setLoading(true);
        // If redirect is appointment, table is taken as prereg
        if (params.redirect && params.redirect === 'appointment') {
            params.table = 'prereg';
        }
        // If redirect is registration, table is taken as opreg
        else if (params.redirect && params.redirect === 'registration') {
            params.table = 'opreg';
        }
        // If the case of complete profile, table is taken as opreg
        if (params.complete_profile && !params.repeat) {
            params.table = 'doctor_appointment';
        }
        // let result = await Opreg.loadGuestInformation(params.id, params.table, params.da_id);
        let loadResults = await Opreg.loadGuestInformation(params.id, params.table, params.da_id || params.appointmentId);

        /*check whether emirates id presents and set results data */
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

        // Extract needed fields
        const loggedInUserId = Number(userInfo.id);
        const userEmail = userInfo?.email;

        const createdUserId = Number(loadResults.data.created_user_id);
        const createdUserEmail = loadResults.data.created_user; // assuming this is the email

        const otherDetails = JSON.parse(userInfo?.other_details || '{}');
        const canCrossEdit = otherDetails?.can_crossedit; // true, false, or undefined

        // Check if appointment belongs to the user
        const isOwnAppointment = createdUserId === loggedInUserId || createdUserEmail === userEmail;

        // Check Existing Guest
        const isExistingGuest = normalizeParam(params?.mode) === 'Existing Customer' ?? false;

        let isEditable = false;

        if (canCrossEdit === true) {
            // Case 1: Cross-edit allowed → editable
            isEditable = true;
        } else if (canCrossEdit === false && isOwnAppointment === true) {
            // Case 2: Cross-edit denied, but own appointment → editable
            isEditable = true;
        } else if (canCrossEdit === false && isOwnAppointment === false && !isExistingGuest) {
            // Case 3: Cross-edit denied, others' appointment → read-only
            isEditable = false;
        } else {
            // Case 4: canCrossEdit is missing (undefined) → fallback
            if (isExistingGuest) {
                isEditable = true;
            } else if (isOwnAppointment) {
                isEditable = true;
            } else {
                isEditable = false;
            }
        }

        // Apply to Remarks field
        setIsRemarksDisabled(!isEditable);

        let result = emiratesDetails ? emiratesDetails : loadResults.data;

        // If coupon is applied, set coupon only for edit cases
        // For existing customer flow, this step is to be avoided
        // If couponno is present, this indicates coupon was applied
        // Else disocunt is applies
        if (result && result.coupon && params.edit) {
            if (result.coupon && result.couponno)
                setCoupon({
                    coupon_value: result.coupon,
                    coupon_amount: result.coupon,
                    coupon_no: result.couponno,
                });
            else if (result.coupon && !result.coupono)
                setDiscount({
                    discount: result.coupon,
                });
        }
        if (result && result.billValue >= 0)
            // Set bill value to total
            setTotal(result.billValue);
        // If not for edit cases, refferer should be made null, as the data here is got from doctor appointment table
        if (!params.edit && params.table === 'prereg' && !params.redirect) {
            result = {
                ...result,
                camp_site: null,
                remarks: null,
            };
        }

        // when camp_site is there need to disable
        // if (result.camp_site) {
        //     let referrer_code = result.camp_site;

        //     // Find the referrer object where `refr_code` matches the given `corporateReferrer`
        //     findAndHandleReferralTypeChange(mergedReferrerData, referrer_code);

        //     fields.camp_site.disabled = props.disableReferrer;
        //     fields.referral_type.disabled = props.disableReferrer;
        // }

        // Set mobile number and country code
        // Both mobile_number and alternate_mobile are set according to the phone input
        var mobile_number = await validatePhoneNumber(result.mobile_number, result.countrycode);
        var alternate_mobile = await validatePhoneNumber(result.alternate_mobile, result.countrycode2);

        //if mobile number have value
        if (mobile_number.value) {
            result = {
                ...result,
                mobile_number,
                alternate_mobile,
                category: 'GEN',
            };
        }

        if (result.date_of_birth && result.date_of_birth.dob) {
            result.date_of_birth = {
                ...result.date_of_birth,
                dob: result.date_of_birth.dob,
            };
        } else {
            result.date_of_birth = {
                ...result.date_of_birth,
                dob: null,
            };
        }

        setInitialValues(result);
        setGenderItem(result.gender); // to set gender validation for registration

        // Set initial values to prepopulate
        form.setFieldsValue(result);
        // when camp_site is there need to disable
        if (result.camp_site) {
            // form.setFieldsValue({ camp_site: referrer_code });
            let referrer_code = result.camp_site;

            if (props.disableReferrer) {
                fields.camp_site.disabled = props.disableReferrer;
                fields.referral_type.disabled = props.disableReferrer;
            }

            // Find the referrer object where `refr_code` matches the given `corporateReferrer`
            await findAndHandleReferralTypeChange(mergedReferrerData, referrer_code);
        }

        // get corporate from logged-in user
        const userCorporate = user?.corporate || null;

        if (result.corporate_pointer) {
            // case 1: corporate pointer from guest info
            setCorporate(true);
            setCorporateValue(result.corporate_pointer);
            setCorporateDisabled(true);

            //  prepopulate bill value and handle coupons only in this case
            await onSelectChange(result.corporate_pointer, false, result.couponno);
        } else if (userCorporate) {
            // case 2: fallback to user corporate (no prepopulate)
            const corporateCodes = Array.isArray(userCorporate) ? userCorporate : [userCorporate];
            setCorporate(true);

            // Handle corporate code logic based on number of codes found
            if (corporateCodes.length === 1) {
                // Only one possible corporate code → auto-select and lock the field
                const corporateCode = corporateCodes[0];
                // Update local state
                setCorporateValue(corporateCode);
                // Reflect in form
                form.setFieldsValue({ corporate_pointer: corporateCode });
                // Disable dropdown
                setCorporateDisabled(true);
            } else if (corporateCodes.length > 1) {
                // Multiple corporate codes → user must choose one
                setCorporateDisabled(false);

                // Fetch all active corporate codes
                const response = await Customers.get({ field: 'cc_active', value: 'Y' });
                const data = response.result || response;

                // Filter only codes relevant to the current user
                const matchedCorporates = data.filter((corp) => corporateCodes.includes(corp.cc_code));

                // Populate dropdown options with matching corporate entries
                setCorporateOptions(
                    matchedCorporates.map((corp) => ({
                        label: corp.cc_desc,
                        value: corp.cc_code,
                    }))
                );
            }
        } else {
            // case 3: no corporate anywhere
            setCorporate(false);
            setCorporateValue(null);
            setSelectCorporate(null);

            await onSelectChange(null);
        }
        // Check if referral_type exists
        if (result.referral_type) {
            // console.log("mergedReferrerData",mergedReferrerData)
            form.setFieldsValue({ referral_type: result.referral_type });

            if (result.camp_site && fields.limit_by_source.visible) {
                // Case: Otherwise, pass mergedReferrerData too
                await handleReferralTypeChange(result.referral_type);
                form.setFieldsValue({ camp_site: result.camp_site });
            } else {
                // Case: Otherwise, pass mergedReferrerData too
                await handleReferralTypeChange(result.referral_type, mergedReferrerData);
            }
            // setFilterReferrer(true);
        }
        if (config) setLoading(false);
    }

    /**
     *
     * @returns
     */
    function validatePhoneNumber(mobile_number, countrycode) {
        // Trim mobile number if any white space is included
        if (mobile_number) {
            // mobile_number = mobile_number.trim()
            // Remove white space from mobile number and country code
            mobile_number = mobile_number.replace(/\s+/g, '');
            mobile_number = mobile_number.replace(/-/g, '');
            mobile_number = mobile_number.replace(/\+/g, '');
        }
        if (countrycode) {
            countrycode = countrycode.replace(/\s+/g, '');
            countrycode = countrycode.replace(/\+/g, '');
        }
        // 1. Two Columns - mobile + country_code | Component || API || Form

        // 2. Single Column = With Country Code

        // 3. Single Column - Without Country Code

        let phoneNumber = {};

        let valid = false;

        if (mobile_number && countrycode && (parseFloat(countrycode) || parseFloat(countrycode) >= 0)) {
            // Joining country code with mobile number
            let numberWithCode = countrycode.concat(mobile_number);
            // checking the number is valid
            if (countrycode == 91) {
                if (numberWithCode.length == 12)
                    // India
                    valid = true;
            } else if (countrycode == 971) {
                // UAE
                if (numberWithCode.length == 12) valid = true;
            } else if (countrycode == 966) {
                //KSA
                if (numberWithCode.length == 12) valid = true;
            } else if (countrycode == 976) {
                // Mongolia
                if (numberWithCode.length == 11) valid = true;
            } else if (countrycode == 65) {
                // Singapore
                if (numberWithCode.length == 10) valid = true;
            } else if (countrycode == 1) {
                // USA Aand Canada
                if (numberWithCode.length == 11) valid = true;
            } else if (countrycode == 61) {
                // Australia
                if (numberWithCode.length == 11 || numberWithCode.length == 12) valid = true;
            } else if (countrycode == 974) {
                //Qatare
                if (numberWithCode.length == 11) valid = true;
            } else if (countrycode == 81) {
                //Japan
                if (numberWithCode.length > 10) valid = true;
            } else if (countrycode === '+00' && numberWithCode.length <= 13) {
                // For cases without countrycode, country code is adde as'+00'
                valid = true;
            } else {
                valid = true;
            }

            phoneNumber = {
                value: mobile_number,
                code: {
                    dialCode: countrycode,
                },
                valid: valid,
            };
        } else if (!countrycode && mobile_number && mobile_number.length < 10) {
            phoneNumber = {
                value: mobile_number,
                code: {
                    dialCode: '+00',
                },
                valid: true,
            };
            setDisableCountryCode(true);
        } else {
            if (mobile_number && mobile_number.length == 10) {
                phoneNumber = {
                    value: mobile_number,
                    code: {
                        dialCode: '91',
                    },
                    valid: true,
                };
                setDisableCountryCode(true);
            } else if (mobile_number && mobile_number.indexOf('91') !== -1 && mobile_number.length === 12) {
                phoneNumber = {
                    value: mobile_number.substring(2, mobile_number.length),
                    code: {
                        dialCode: '+91',
                    },
                    valid: true,
                };
            } else if (mobile_number && mobile_number.indexOf('971') !== -1 && mobile_number.length === 12) {
                phoneNumber = {
                    value: mobile_number.substring(3, mobile_number.length),
                    code: {
                        dialCode: '+971',
                    },
                    valid: true,
                };
            } else if (mobile_number && mobile_number.indexOf('976') !== -1 && mobile_number.length === 11) {
                phoneNumber = {
                    value: mobile_number.substring(3, mobile_number.length),
                    code: {
                        dialCode: '+976',
                    },
                    valid: true,
                };
            }
        }

        return {
            ...phoneNumber,
        };
    }

    /**
     * On submission of the form
     */
    async function onFinish({ verified, existing_user }) {
        let values = form.getFieldsValue();

        values = {
            ...values,
            is_nuradesk: true,
            existing_user: existing_user,
        };

        // Check if a user already exists and the setting is 'NAP'
        if (existing_user) {
            values.existing_user = true; // Ensure 'existing_user' is true when a user exists
        }
        // If verified is true(when the appointment create, is triggered but a appointmentm with same name and email or name and mobile number is present in appointment and the customer has approved for appointment)

        if (verified) {
            values.verified = true;
        }

        let queryParam = {};
        const sessionId = localStorage.getItem('SessionId');
        setBtnLoading(true);
        let appointment_mode;

        // if (!params.edit) {
        const genderValidation = await Opreg.validateGender(values.gender, genderItem);

        // If gender validation is false, selectedngender does not match the gender specified for the selected package
        if (!genderValidation) {
            message.error('The Selected Gender does not match with the Selected Package Gender. Please Select the Correct Gender');
            setBtnLoading(false);
            return false;
        }
        // }

        // Append additional key in case of stoptb
        values = {
            ...values,
            addition_key_id: additionalkey,
            source: 'DESK',
            session_id: sessionId,
        };

        // If address is not entered save it as an empty string
        // if (!values.address1) values.address1 = '';
        // if (!values.address2) values.address2 = '';

        // Get appointmentMode
        if (props.appointmentMode) {
            // For nura the attribute evalRequired is present, and we dont need to eval the appointmentmode
            if (props.evalRequired) {
                appointment_mode = props.appointmentMode;
            } else {
                appointment_mode = eval(props.appointmentMode)();
            }
        }
        //  Conditionally add syncRegistration
        if (appointment_mode !== 'APPWPREREG' && appointment_mode !== 'EDITPREREG') {
            values = {
                ...values,
                syncRegistration: syncReg?.syncRegistration,
            };
        }
        /**Get mobile number and countrycode from the entered data */
        // Storing value of op_mobile to a variable
        var mobile = values.mobile_number;

        var dialCodeLength,
            mobile_number,
            countryCode = null;

        //Storing country code length
        dialCodeLength = mobile.code.dialCode.length;

        if (mobile.value.length > 10) mobile_number = mobile.value.substring(dialCodeLength);
        else mobile_number = mobile.value;

        countryCode = mobile.code.dialCode;

        var alternate_mobile;

        // If alternate Mobile number is entered, get the mobile number from the value by avoiding the country code
        if (values.alternate_mobile && values.alternate_mobile.value)
            if (values.alternate_mobile.value.length > 10)
                alternate_mobile = values.alternate_mobile.value.substring(values.alternate_mobile.code.dialCode.length);
            else alternate_mobile = values.alternate_mobile.value;

        const time = moment(params.selected_time, 'DD/MM/YYYY hh:mm A').valueOf();

        values = {
            ...values,
            package_value: params.da_packagetypevalue,
            doctor: params.da_doctorptr,
            token_no: params.token_no,
            age_type: values.date_of_birth.age_type,
            age: values.date_of_birth.age,
            age_day: values.date_of_birth.day,
            age_month: values.date_of_birth.month,
            age_year: values.date_of_birth.year,
            mobile_number: mobile_number,
            countrycode: countryCode,
            dob: values.date_of_birth.dob ? moment(values.date_of_birth.dob).format('YYYY/MM/DD') : null,
            opno: initialValues && initialValues.opno ? initialValues.opno : null,
            // Visit Remarks
            visit_remarks: params.remarks,
            total: total,
            discount: discount,
            package_type: 'ITM',
            camp_site: values.camp_site ?? initialValues?.camp_site ?? null,
            referral_type: values.referral_type ? values.referral_type : '-1',

            // op_iscorporate: 'N',

            // mode_of_communication: '3',
            // op_dispage: 'null',
        };

        // I f alternate mobile number is present save country code and mobile number
        if (values.alternate_mobile && values.alternate_mobile.value) {
            values = {
                ...values,
                alternate_mobile: alternate_mobile,
                countrycode2: values.alternate_mobile.code.dialCode,
            };
        } else {
            values = {
                ...values,
                alternate_mobile: null,
                countrycode2: null,
            };
        }
        //  check corporate value
        if (corporate) {
            values.corporate = corporate;
        } else {
            values.corporate_pointer = null;
            values.corporate_information = null;
        }

        // Check if the entered phone number is valid or not
        if (values.mobile_number && values.countrycode) {
            const phoneValidation = await validatePhoneNumber(values.mobile_number, values.countrycode);
            // If not valid, show an error message
            if (phoneValidation.valid) {
            } else {
                message.error('Please Check the Entered Phone Number');
                setBtnLoading(false);
                return false;
            }
        } else {
            // This else condition include based on prod issue.
            message.error('Please Enter Phone Number');
            setBtnLoading(false);
            return false;
        }

        // Check if the entered alternate phone number is valid or not
        if (values.alternate_mobile && values.countrycode2) {
            const phoneValidation = await validatePhoneNumber(values.alternate_mobile, values.countrycode2);
            // If not valid, show an error message
            if (phoneValidation.valid) {
            } else {
                message.error('Please Check the Entered Phone Number');
                setBtnLoading(false);
                return false;
            }
        }

        // If coupon is present
        // To show total in summary, coupon value is reduced from total,when coupon is applied
        // On submission, the coupon is validated once more in backend and if that is a success, coupon value is deducted from da_billvalue ie, total
        // So we are adding back the coupon value as it would be deducted from backend once again
        if (coupon && Number(coupon.coupon_amount) >= 0) {
            values = {
                ...values,
                coupon: coupon,
            };
        }
        if (discount && discount.discount) {
            values = {
                ...values,
                disocunt: discount.discount,
            };
        }
        // If selected_time is present in url, append appointment date and time
        if (params.selected_time) {
            values = {
                ...values,
                appointment_date: moment(time).format('YYYY-MM-DD'),
                appointment_time: moment(time).format('hh:mm A'),
            };
        }
        // In case of existing customer flow, add prereg or opreg id to the values
        if (params.table === 'prereg') {
            values = {
                ...values,
                prereg_id: params.id,
            };
        } else if (params.table === 'opreg') {
            values = {
                ...values,
                opreg_id: params.id,
            };
        }

        /**Get all the add_on fields and it's corresponding values */
        // Get keys of values entered in the form
        const fields = Object.keys(values);
        // Get values of values entered in the form
        const value = Object.values(values);

        // Get field name from add_on_fields
        const extraFields = Object.values(add_on_fields);

        // For each add_on_field element, get the values entered and store it in an array
        // This array is used to store data in op_otherdetails1in xml format
        if (extraFields && extraFields.length)
            extraFields.map((ele) => {
                const index = fields.indexOf(ele.field);
                if (value[index] !== undefined)
                    additionalValues.push({
                        field: ele.field,
                        value: value[index],
                    });
            });

        // Add the array with additinal values
        values = {
            ...values,
            additionalValues: additionalValues,
        };

        // ExtraParams for billing is passed as attribute, this includes item to be billed,rate and quantity
        if (props.extraBillingParams) {
            values = {
                ...values,
                ...props.extraBillingParams,
            };
        }

        // Append appointment mode
        if (props.appointmentMode) {
            values = {
                ...values,
                appointment_mode: appointment_mode,
                mode: props.mode,
                appointmentId: params.appointmentId,
                id: params.id,
            };
        }
        // in the case icarus flow need  opreg
        if (params.complete_profile) {
            values = {
                ...values,
                da_id: params.da_id,
            };
        }

        // in the case icarus flow need  opreg
        if (params.complete_profile) {
            values = {
                ...values,
                da_id: params.da_id,
            };
        }
        // For edit of appointment provide doctor appointmnet id
        if (params.edit && params.redirect === 'appointment') {
            values = {
                ...values,
                id: params.da_id,
            };
        }

        if (values.date_of_birth && values.date_of_birth.dob) {
            let dob = moment(values.date_of_birth.dob).format('YYYY-MM-DD');

            // values.date_of_birth.dob = moment(dob, 'DD/MM/YYYY');
            values.date_of_birth.dob = dob;
        }

        const response = await Opreg.saveNewRegistration(values);

        let result = response.result;

        //    if (result.success) {
        if (result) {
            let opno;

            // 1. Appointment Create- appointment/:id
            // 2. Appointment Edit - appointment/:id

            // 3. Repeat Appointment with Prereg - appointment/:id
            // 4. Repeat Appointment with Opreg - appointment/:id

            // 5. Registration Add - registration/:id
            // 6. Registration Edit - registration/:id

            // 7. Registration from Appointment - /multiple-candidate/bill/opno=${opno}&appointmentId=${id}

            // For nura-desk, redirect to appointment detail page

            if (props.redirect) {
                // For registration add and edit of nura
                // if (props.evalRequired) {

                //     // opno = result.result.op_no

                //     opno = result.op_no
                // } else {

                //     //  #NOTE Sneha - Used for appointment flow

                //     opno = result.opReg.op_no
                // }

                if (params.appointmentId) {
                    result = {
                        ...result,
                        appointmentId: params.appointmentId,
                    };
                }

                let keys = Object.keys(result);

                let finalRedirectUrl;

                keys.forEach((key) => {
                    if (key === 'op_no' || key === 'da_id') {
                        finalRedirectUrl = props.redirect.replace(':id', result[key]);
                    }
                    if (key === 'appointmentId') {
                        finalRedirectUrl = finalRedirectUrl.replace(':appointmentId', result[key]);
                    }

                    console.log(finalRedirectUrl);
                });

                Location.navigate({ method: 'replace', url: `${finalRedirectUrl}` });

                setBtnLoading(false);

                // Location.navigate({ url: props.redirect + result.da_id })

                // In case of new registration it will redirect to billing page else in case of edit registration redirect back to registration detail page (in nura)
                // if (props.appointmentMode === 'OPREG') {

                // Location.navigate({ url: `/multiple-candidate/bill?op_no=${opno}` });

                // } else {

                //     Location.navigate({ url: `/registrations/${opno}` });
                // }
            } else {
                //For stoptb visit and bill is created
                if (props.extraBillingParams) {
                    setBtnLoading(false);

                    Location.navigate({ url: `/patient-visits-new` });
                } else {
                    //For alsalama navigate to billing page
                    Location.navigate({ url: `/multiple-candidate/bill`, queryParam });

                    setBtnLoading(false);
                }
            }
        } else {
            //for already taken appointment
            if (response.setting) {
                if (response.verified === true) {
                    if (response.setting === 'NAP') {
                        onFinish({});
                    } else if (response.setting === 'MND') {
                        setAlreadyBooked(false);
                    } else {
                        setAlreadyBooked(true);
                    }
                } else {
                    // Check the value of the setting property in the response object
                    if (response.setting === 'MND') {
                        // If setting is 'MND', set mandatory setting and already booked to true
                        setMandatorySetting(true);
                        setAlreadyBooked(true);
                    } else if (response.setting === 'NAP') {
                        // If setting is 'NAP', set already exist to true
                        setAlreadyExist(true);
                    } else {
                        // For any other setting, set already booked to true
                        setAlreadyBooked(true);
                    }
                }
            }
            //for other issues(eg:slot already taken)
            else {
                setBtnLoading(false);
                message.error(response.message);
            }
        }
    }
    /**
     * Function triggered on corporate selection change.
     * Updates the bill value and referrer field.
     * @param {*} value
     */
    async function onSelectChange(value, isLoading = false, couponNo = null) {
        let record;
        if (params.da_packagetypevalue) {
            record = {
                // set package value as array .
                // it's maintained for billing screen functionality
                package_value: [params.da_packagetypevalue],
                type: props.corporate_type,
                corporate_pointer: value,
            };
        }

        // get call

        try {
            // Fetch corporate rate mapping
            let corporateRate = await Items.getItemRateMapping(record);
            if (corporateRate.success) {
                // If there is a coupon then we have to apply it on corporate amount
                if (coupon?.coupon_no || couponNo) {
                    let formBody = {
                        coupon_no: coupon && coupon.coupon_no ? coupon.coupon_no : couponNo,
                        item: params.da_packagetypevalue,
                        booking_date: params.selected_date,
                        applicable_on: 'MED',
                    };
                    let couponDetails = await Appointments.applyCoupon(formBody);

                    let couponAmount = 0;
                    let totalAmount = 0;
                    // Check if coupon type is percetage or amount
                    if (coupon.value_type === 'P') {
                        couponAmount = corporateRate.data[0].rate * (coupon.coupon_value / 100);
                    } else if (coupon.value_type === 'A') {
                        couponAmount = coupon.coupon_value;
                        // Check if coupon details are returned successfully or if the initial coupon number matches the one in the coupon object
                    } else if (couponDetails.success || initialValues.couponno === coupon.coupon_no) {
                        if (couponDetails.data.value_type === 'P')
                            couponAmount = corporateRate.data[0].rate * (couponDetails.data.coupon_value / 100);
                        else if (couponDetails.data.value_type === 'A') couponAmount = couponDetails.data.coupon_value;
                    }
                    totalAmount = corporateRate.data[0].rate - couponAmount;
                    setTotal(totalAmount);
                } else {
                    setTotal(corporateRate.data[0].rate);
                }
            }

            if (props.enableCorporateReferrerMap) {
                //  // If the selected corporate pointer is the same as the current one, skip API call
                //  if (value === initialValues.corporate_pointer) {

                //     return;
                // }

                let referrer;
                let corporateReferrer;

                // Fetch the referrer mapping based on the corporate
                if (value) {
                    referrer = await Items.getCorporateReferrerMapping(value);
                }

                let updatedFields = {
                    ...fields,
                    camp_site: { ...fields.camp_site },
                    referral_type: { ...fields.referral_type },
                    limit_by_source: { ...fields.limit_by_source },
                };

                // If referrer is found, set it and disable the field
                if (referrer && referrer.id) {
                    corporateReferrer = referrer?.id;

                    if (isLoading && props.showCorporateChangePopup) {
                        Modal.confirm({
                            title: 'Confirmation',
                            content: 'Are you sure you want to update referrer according to the corporate ?',
                            // okText: "Yes",
                            // cancelText: "No",
                            onOk: () => {
                                setRefLoading(true); // Start loading
                                // Find the referrer object where `refr_code` matches the given `corporateReferrer`
                                findAndHandleReferralTypeChange(mergedReferrerData, corporateReferrer);
                                form.setFieldsValue({ camp_site: corporateReferrer }); // Set referrer value
                                updatedFields.camp_site.disabled = true; // Disable referrer field
                                updatedFields.referral_type.disabled = true; // Disable referrer field
                                updatedFields.limit_by_source.disabled = true;
                                setTimeout(() => {
                                    setRefLoading(false); // Stop loading after setting referrer
                                }, 1000);
                            },
                        });
                    } else {
                        // Find the referrer object where `refr_code` matches the given `corporateReferrer`
                        findAndHandleReferralTypeChange(mergedReferrerData, corporateReferrer);
                        form.setFieldsValue({ camp_site: corporateReferrer }); // Set referrer value
                        updatedFields.camp_site.disabled = true;
                        updatedFields.referral_type.disabled = true; // Disable referrer field
                        updatedFields.limit_by_source.disabled = true;
                        // Disable referrer field
                    }

                    // setTimeout(() => {
                    //     setRefLoading(false); // Stop loading after setting referrer
                    // }, 1000);
                } else {
                    updatedFields.camp_site.disabled = false;
                    updatedFields.referral_type.disabled = false; // Disable referrer field
                    updatedFields.limit_by_source.disabled = false;

                    // If there was an initial value, restore it
                    if (initialValues.camp_site) {
                        // form.setFieldsValue({ camp_site: initialValues.camp_site });
                    }
                    setRefLoading(false);
                }

                // Apply updated field validation state
                setValidation(updatedFields);
                // setRefLoading(false); // Stop loading
            }
        } catch (error) {
            setRefLoading(false); // Stop loading in case of error
        }
    }

    // Show referrer loading  only when `refLoading` is true
    useEffect(() => {
        if (refLoading) {
            const hideMessage = message.loading({
                content: 'Fetching Referrer...',
                duration: 0, // Message stays until manually destroyed
                style: {
                    marginTop: '20vh',
                    textAlign: 'center',
                    fontSize: '16px',
                },
            });

            // Set timeout to remove loading after 3 seconds
            const timeoutId = setTimeout(() => {
                hideMessage(); // Destroy the loading message
            }, 5000);

            return () => clearTimeout(timeoutId); // Cleanup on unmount or state change
        } else {
            message.destroy(); // Remove any existing loading message when refLoading is false
        }
    }, [refLoading]);
    /**
     * The function `handleReferralTypeChange` filters a list of referrers based on a given value and
     * updates the referral options accordingly.
     * @param value - The `value` parameter in the `handleReferralTypeChange` function is the new value
     * selected for the referral type. This value is used to filter the `referrerLists` to get a subset of
     * referrers that match the selected referral type. The filtered result is then set as the new
     */
    const handleReferralTypeChange = async (value, referrerLists = null) => {
        /*Set initial loading to load all referrer list if referrertype is unselected  */
        let initialLoading = referrerLists && value ? true : false;
        referrerLists = referrerLists || (await initialReferrerLists());
        // Filter referrer list by the selected referral type
        let result = referrerLists.filter((item) => item.refr_typecode == value);
        //If matching results found and it's not the initial loading phase
        if (result.length && !initialLoading) {
            //Set filtered referrer options in state
            setReferrerOptions(result);
            //Get current selected camp_site from form
            let referrer = form.getFieldValue('camp_site');
            //Check if the current camp_site is in the new result
            const foundReferrer = result.find((item) => item.refr_code === referrer?.refr_code);
            // If not found, reset the form field 'camp_site'
            if (!foundReferrer) form.setFieldValue('camp_site', '');
            // else form.setFieldValue('camp_site', foundReferrer.refr_code) // Update referral with null value
        } else {
            //If no results found
            if (result.length === 0) {
                // Clear the camp_site field and options
                form.setFieldValue('camp_site', '');
                setReferrerOptions([]);
            } else {
                // If results exist but it's initial load, show all
                setReferrerOptions(referrerLists);
            }

            // form.setFieldValue('camp_site', '');
            // Update referral with null value
            //// Update referral with null value
        }
        // Always update the referral_type field in the form if value is not null
        if (value !== null) form.setFieldValue('referral_type', value);
        // Update referral type
    };

    /**
     * Function to be feeded for reference select
     * to default the referrer for all
     * cases the one which has the lowest refr_code
     *
     * @param {*} options
     * @returns
     */
    const identifyDefaultReferrer = (options = []) => {
        // Skip setting default if corporate mapping exists
        if (props.enableCorporateReferrerMap && corporate && !user.corporate) return null;
        // Set initial referrer (priority: user.referrer > initialValues.camp_site > default "1")
        let initial;

        /**If there is userReferrer or referrer value is not null then the default referrer of user is taken */
        if (props.userReferrer || initialValues.camp_site !== null) {
            initial = initialValues.camp_site ? initialValues.camp_site : user?.referrer && user.referrer !== '' ? user.referrer : '1';

            /** else saved referrer is prepopulated or else default referrer*/
        } else if (initialValues.camp_site) {
            initial = initialValues.camp_site ? initialValues.camp_site : '1';
        } else {
            // default loading self referrer
            initial = '1';
        }

        // Find matching referrer
        let matching = options.find((record) => record.refr_code == initial);
        let referrer_code = matching ? matching.refr_typecode : null;
        // findAndHandleReferralTypeChange(options,referrer_code);

        if (matching) {
            options = options.filter((item) => item.refr_typecode == matching.refr_typecode);
        }

        // if (initialValues.referral_type != '-1') {
        //     // setFilterReferrer(true)
        //     handleReferralTypeChange(referrer_code, options);

        // }

        if (!initialValues.referral_type) {
            // setFilterReferrer(true)
            // handleReferralTypeChange(referrer_code, options);
            if (fields.limit_by_source.visible && fields.limit_by_source.default_value) {
                handleReferralTypeChange(referrer_code, options);
            } else {
                handleReferralTypeChange(referrer_code, mergedReferrerData);
            }
        }

        //  If a referrer is found, update referrer type as well
        return matching;
    };

    /**
     * The function `findAndHandleReferralTypeChange` searches for a specific referrer code in a list
     * and then handles a referral type change if the code is found.
     * @param referrerList - An array of objects containing referral information. Each object should
     * have properties like `refr_code` and `refr_typecode`.
     * @param referrerCode - The `referrerCode` parameter is the code that is used to identify a
     * specific referrer in the `referrerList`. It is used to find a specific referrer object in the
     * list based on this code.
     */

    const findAndHandleReferralTypeChange = async (referrerList, referrerCode) => {
        if (referrerList && referrerList.length) {
            let referralType = form.getFieldValue('referral_type');
            const foundReferrer = await referrerList.find((item) => item.refr_code === referrerCode);
            if (referralType && referrerCode) {
                if (referralType === foundReferrer.refr_typecode && fields.limit_by_source.visible) {
                    setFilterReferrer(true);
                    let filtedreferrer = referrerList.filter((item) => item.refr_typecode == foundReferrer.refr_typecode);
                    await handleReferralTypeChange(foundReferrer.refr_typecode, filtedreferrer);
                } else {
                    setFilterReferrer(false);
                    setReferrerOptions(referrerList);
                }
            }

            // if (foundReferrer && foundReferrer.refr_typecode) {
            //     let filtedreferrer = referrerList.filter((item) => item.refr_typecode == foundReferrer.refr_typecode);
            //     console.log('PPPPPPPPPPPPPPPPPPPPPPPPPP')
            //     await handleReferralTypeChange(foundReferrer.refr_typecode, filtedreferrer);
            // }
        }
    };

    /**
     * The function `initialReferrerLists` asynchronously fetches referrer lists and returns the result,
     * handling errors by logging them.
     * @returns The function `initialReferrerLists` is returning the result of the `Referrers.get()` call
     * if it is successful. If there is an error during the call, it will log the error message and return
     * an empty array `[]`.
     */
    const initialReferrerLists = async () => {
        try {
            const response = await Referrers.get();
            return response.result;
        } catch (error) {
            return [];
        }
    };

    // set dob validation in registration page . The Date of Birth should be Mandatory in the registration screen, even if the Age is given
    // let dobValidation = false;

    // if (params.redirect && !params.edit) {

    //     dobValidation = true;

    // }

    // Note : We are adding this common set of rules to disallow reserved variables to the input
    // This however is a temporary hack . As we need to define a common / extendable component
    // that needs to be used as a base component that would filter any input data to avoid SQL injection or
    // any threat .
    let commonInputRules = [
        {
            pattern: /^(?:(?!').)*$/,
            message: "' is not allowed!",
        },
    ];

    // Maintaining a prop to control latin characters in Name
    let enableAllCharacters = props.enableAllCharacters;

    // Enable the latin
    if (!enableAllCharacters) {
        // To the common input rules , we need to concat additional controls
        commonInputRules = [].concat(commonInputRules, {
            pattern: /^[a-zA-Z\s]*$/g,
            message: t('Please Enter Valid Name!'),
        });
    }
    /* The code is accessing the value of a form field with the name 'title' using the
`getFieldValue` method of the `form` object. Its for , title is 'empty' case and manage with attribute 'editieldsDisable' for editable and noneditable */
    // Access form values
    // const titleValue = form.getFieldValue('title');
    // //  Determine if the Select should be disabled case of
    // const isDisabled = titleValue ? props.editFieldsDisable : false;

    /* The code is accessing the value of a form field with the name 'title' using the
`getFieldValue` method of the `form` object. Its for , title is 'empty' case and manage with attribute 'editieldsDisable' for editable and noneditable */
    // Access form values
    const titleValue = form.getFieldValue('title');

    //  Determine if the Select should be disabled case of
    const isDisabled = titleValue ? props.editFieldsDisable : false;

    // Fetch active corporates from API then filter to find the corporate that matches the current
    useEffect(() => {
        if (initialValues?.corporate_pointer) {
            Customers.get({ field: 'cc_active', value: 'Y' }).then((response) => {
                // Extract result from response or fallback to response directly
                let data = response.result;
                const filterdData = data.find((data) => data?.cc_code === initialValues?.corporate_pointer);
                setSelectCorporate(filterdData);
            });
        }
    }, [initialValues]);

    /**
     *  Toggle the state of the referrer filter checkbox
     * Get the current 'camp_site' value from the form
     *Get the current 'referral_type' value from the form
     *Fetch the initial referrer list
     *If the checkbox is being UNCHECKED
     *    Reset referrer options to the initial referrer list
     *  If 'camp_site' has a value
     *      Show a confirmation dialog
     *          If confirmed: reset referrer options and clear 'camp_site' field
     *          If cancelled: reset referrer options
     *   If 'camp_site' is empty
     *       Call 'handleReferralTypeChange' with the current referral type and referrer list
     * Else (checkbox is being CHECKED)
     *    Get the 'referral_type' from the form
     *   Call 'handleReferralTypeChange' with the current referral type
     * @param {*} values
     */

    async function filterByReferrer(values) {
        // Toggle the checkbox state for "Filter by Referrer"
        setFilterReferrer(!filterReferrer);
        let referrer = form.getFieldValue('camp_site');
        let value = form.getFieldValue('referral_type');
        let referrerLists = await initialReferrerLists();
        // If checkbox is unchecked, load and reset the initial referrer list
        if (!values.target.checked) {
            setReferrerOptions(referrerLists);
            if (referrer) {
                Modal.confirm({
                    // title: 'Change Referrer Filter Setting',
                    content: 'Unchecking this option will remove the referrer which is already selected. Do you want to proceed?',
                    okText: 'Yes',
                    cancelText: 'No',
                    onOk: () => {
                        setReferrerOptions(referrerLists);
                        // You can call handleReferralTypeChange or any other logic here if needed
                        // handleReferralTypeChange(value,referrerLists);
                        form.setFieldsValue({ camp_site: '' });
                    },
                    onCancel: () => {
                        setReferrerOptions(referrerLists);
                    },
                });
            } else {
                // if (!referrer) {
                handleReferralTypeChange(value, referrerLists);
                // }
            }

            // let value = form.getFieldValue('referral_type');
            // handleReferralTypeChange(value);
        } else {
            handleReferralTypeChange(value);
        }
        // else {
        //     let referral_type = form.getFieldValue('referral_type');
        //     handleReferralTypeChange(referral_type);
        //     // If checkbox is checked, trigger change handler for the current referral_type value
        // }
    }

    return loading ? (
        <Card>
            <Skeleton />
        </Card>
    ) : (
        <div className="registration-screen">
            {/** Guest card section start */}
            <Card className="guest-details ">
                {initialValues && props.couponSection !== false ? (
                    <>
                        {/* Summary Section */}
                        <Summary
                            setGender={setGenderItem}
                            hideDiscountButton={props.hideDiscountButton}
                            disableApplyCoupon={props.disableApplyCoupon}
                            hidePriceDetails={props.hidePriceDetails}
                            appliedDiscount={discount}
                            appliedCoupon={coupon}
                            totalValue={total}
                            billValue={initialValues.billValue}
                            corporate={corporate}
                            setItems={setItems}
                            edit={params.edit}
                            callback={(values, mode, total, result) => {
                                // let field = form.getFieldsValue(camp_site);
                                /** Corresponding corporate should be auto populated when typing a coupon */
                                if (result?.success && result.data?.corporate_ptr) {
                                    // Indicate that the corporate pointer field is relevant and should be used
                                    setCorporate(true);

                                    // Set form values dynamically
                                    form.setFieldsValue({ corporate_pointer: result.data.corporate_ptr });

                                    // Directly update fields since it's a constant
                                    fields.corporate_pointer.corporate_disable = true;
                                }

                                /** Corresponding referrer should be auto populated when typing a coupon */
                                if (result?.success && result.data?.referrer_ptr) {
                                    // Set form values dynamically
                                    form.setFieldsValue({ camp_site: result.data.referrer_ptr });

                                    // Directly update fields since it's a constant
                                    fields.camp_site.referrer_disable = true;
                                }
                                if (mode === 'coupon') {
                                    // If coupon is null, it indicates applied coupon was cancelled
                                    // If coupon is present set discount as null
                                    if (values === null) {
                                        // Check if the coupon object has a referrer_ptr property
                                        if (coupon.referrer_ptr) {
                                            // Reset the value of the camp_site field in the form to null
                                            form.setFieldsValue({ camp_site: null });
                                            // enable referrer field
                                            fields.camp_site.referrer_disable = false;
                                        }

                                        // Check if the coupon object has a corporate_ptr property
                                        if (coupon.corporate_ptr) {
                                            setCorporate(false);
                                            // Reset the value of the corporate_pointer field in the form to null
                                            form.setFieldsValue({ corporate_pointer: null });
                                            //enable the corporate field
                                            fields.corporate_pointer.corporate_disable = false;
                                        }
                                        setCoupon(null);
                                    } else setCoupon(result.data);
                                    setDiscount(null);
                                } else if (mode === 'discount') {
                                    // If discount is null, it indicates applied discount was cancelled
                                    // If discount is present st coupon as null
                                    if (values === null) setDiscount(null);
                                    else setDiscount(values.discount);
                                    setCoupon(null);
                                }
                                setTotal(total);
                            }}
                        />
                        {/* Summary Section Ends */}
                    </>
                ) : null}

                {params.id || params.edit ? null : <ExistingUser />}

                <div>
                    <h3 className="title">Guest Information</h3>
                    <h5 className="title">fill in your information to proceed with your booking.</h5>{' '}
                </div>

                <Form
                    form={form}
                    {...formItemLayout}
                    onValuesChange={onValuesChange}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    initialValues={initialValues}
                >
                    <div className="guest-information">
                        {/** Page Header section start */}
                        {/* <div className="page-header">
                            <div>
                                <Title level={4}>{t('GUEST INFORMATION')}</Title>

                                <p className="page-desc">{t('Please enter the below details to complete the registration.')}</p>
                            </div>
                        </div> */}
                        {/** Page Header section start */}

                        {/** Form Content of patient section start */}
                        <div className="form-content">
                            <div className="steps-content">
                                <Row gutter={[16, 0]}>
                                    {' '}
                                    {/* Adjust gutter spacing as needed */}
                                    {fields.email_address.visible ? (
                                        <Col xs={24} md={12} xl={12} style={{ paddingBottom: 0 }}>
                                            <Form.Item
                                                autoFocus={true}
                                                name="email_address"
                                                label={fields.email_address.caption}
                                                rules={[
                                                    {
                                                        type: 'email',
                                                        message: `The input is not valid ${fields.email_address.caption}`,
                                                    },
                                                    {
                                                        required: fields.email_address.required,
                                                        message: `Please Enter ${fields.email_address.caption}`,
                                                    },
                                                    {
                                                        max: 100,
                                                        message: t('Invalid Email!'),
                                                    },
                                                ]}
                                                validateStatus={validation && validation.email_address.validate_status}
                                                help={validation && validation.email_address.help_text}
                                                // validateStatus={!initialValues.email_address && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.email_address && props.completeProfile ? 'Please fill the details' : ''}
                                                style={{
                                                    marginBottom: '4px',
                                                }}
                                            >
                                                <Input
                                                    mode="lowercase"
                                                    autoFocus={true}
                                                    autoComplete="off"
                                                    disabled={fields.email_address.disabled || props.editFieldsDisable || props.verifiedDetails}
                                                    onBlur={() => onBlur('email')}
                                                    style={{
                                                        marginBottom: '4px', // Remove internal Input spacing (some themes add this)
                                                    }}
                                                />
                                            </Form.Item>
                                            <Row style={{ width: '100%' }}>
                                                <Col xs={24} sm={24} md={24} lg={24} xl={24} style={{ width: '100%' }}>
                                                    {/* Alert when existing customer is found */}
                                                    {alertOnEmail && (
                                                        // <div style={{ marginTop: '0px', lineHeight: '1' }}>
                                                        <Alert
                                                            className="custom-alert"
                                                            message={
                                                                <div style={{ width: '100%', display: 'block' }}>
                                                                    We have found that {existingCustomers.length} accounts are already linked to{' '}
                                                                    <strong>{existingCustomers[0]?.email}</strong>
                                                                </div>
                                                            }
                                                            type="success"
                                                            showIcon
                                                            style={{
                                                                width: '167%',
                                                                boxSizing: 'border-box',
                                                            }}
                                                            action={
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    style={{ pointerEvents: 'auto', zIndex: 1000 }}
                                                                    onClick={() => onBlur('email')}
                                                                >
                                                                    Click to know more
                                                                </Button>
                                                            }
                                                        />
                                                        // </div>
                                                    )}
                                                </Col>
                                            </Row>
                                        </Col>
                                    ) : null}
                                    {params.edit ? null : (
                                        <Col xs={24} md={12} xl={12} style={{ display: 'flex', paddingTop: '30px' }}>
                                            <Button type="primary" onClick={() => onBlur('email')}>
                                                <SearchOutlined />
                                            </Button>
                                        </Col>
                                    )}
                                    {fields.mobile_number.visible ? (
                                        <Col xs={24} md={12} xl={12} style={{ paddingBottom: 0 }}>
                                            <Form.Item
                                                rules={[
                                                    {
                                                        required: fields.mobile_number.required,
                                                        message: `The input is not valid ${fields.mobile_number.caption}`,
                                                    },
                                                    // {
                                                    //     validator(_, value) {

                                                    //         if (value) {
                                                    //             // checking the number is valid or not
                                                    //             let numberValid = validatePhoneNumber(value.value, value.code.dialCode)

                                                    //             if (!numberValid.valid) {

                                                    //                 return Promise.reject('Please provide a valid mobile number');

                                                    //             } else {

                                                    //                 return Promise.resolve();

                                                    //             }
                                                    //         } else {
                                                    //             return Promise.resolve();
                                                    //         }
                                                    //     },
                                                    // },
                                                ]}
                                                name="mobile_number"
                                                label={fields.mobile_number.caption}
                                                className="phone-input"
                                                //validateStatus={!initialValues.mobile_number && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.mobile_number && props.completeProfile ? 'Please fill the details' : ''}
                                                style={{ marginBottom: '4px' }}
                                            >
                                                <CountryPhoneInput
                                                    disableCountryCode={disableCountryCode}
                                                    required={fields.mobile_number.required}
                                                    defaultCountryCode={process.env.REACT_APP_COUNTRYCODE || 'in'}
                                                    disabled={fields.mobile_number.disabled} // Disable the input
                                                    onBlur={() => onBlur('mobile1')} // Trigger search for mobile when blur occurs
                                                    style={{ marginBottom: '4px' }}
                                                    autoComplete="off"
                                                />
                                            </Form.Item>
                                            <Row style={{ width: '100%' }}>
                                                <Col xs={24} sm={24} md={24} lg={24} xl={24} style={{ width: '100%' }}>
                                                    {alertOnPhone && (
                                                        <Alert
                                                            className="custom-alert"
                                                            message={
                                                                <div style={{ width: '100%', display: 'block' }}>
                                                                    We have found that {existingCustomers.length} accounts are already linked to{' '}
                                                                    <strong>{existingCustomers[0]?.mobile1}</strong>
                                                                </div>
                                                            }
                                                            type="success"
                                                            showIcon
                                                            style={{
                                                                width: '165%',
                                                                boxSizing: 'border-box',
                                                            }}
                                                            action={
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    style={{ pointerEvents: 'auto', zIndex: 1000 }}
                                                                    onClick={() => onBlur('mobile1')}
                                                                >
                                                                    Click to know more
                                                                </Button>
                                                            }
                                                        />
                                                    )}
                                                </Col>
                                            </Row>
                                        </Col>
                                    ) : null}
                                    {params.edit ? null : (
                                        <Col xs={24} md={12} xl={12} style={{ display: 'flex', paddingTop: '30px' }}>
                                            <Button type="primary" onClick={() => onBlur('mobile1')}>
                                                <SearchOutlined />
                                            </Button>
                                        </Col>
                                    )}
                                </Row>

                                <Row gutter={20}>
                                    {fields.title.visible ? (
                                        <Col xs={24} md={12} xl={6}>
                                            <Form.Item
                                                name="title"
                                                label={fields.title.caption}
                                                rules={[
                                                    { required: fields.title.required, message: t('Please Select Title') },
                                                    {
                                                        validator(_, value) {
                                                            var genderValid;

                                                            // Validate if the selcted title and gender is suitable for the gender of the item
                                                            if (value === 'Mr.') {
                                                                genderValid = 'Male';
                                                            } else if (value === 'Ms.' || value === 'Mrs.') {
                                                                genderValid = 'Female';
                                                            } else if (value === 'Dr.') {
                                                                genderValid = 'Both';
                                                            } else {
                                                                genderValid = 'Both';
                                                            }
                                                            const validGender = Opreg.validateGender(genderValid, genderItem);
                                                            if (!validGender) {
                                                                return Promise.reject(
                                                                    'This title cannot be used as it does not match with Package gender'
                                                                );
                                                            } else {
                                                                return Promise.resolve();
                                                            }
                                                        },
                                                    },
                                                ]}
                                                validateStatus={validation && validation.title.validate_status}
                                                help={validation && validation.title.help_text}
                                                // validateStatus={!initialValues.title && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.title && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <Select showSearch optionFilterProp="children" disabled={fields.title.disabled || isDisabled}>
                                                    <Option value="Mr.">{t('Mr')}.</Option>
                                                    <Option value="Ms.">{t('Ms')}.</Option>
                                                    <Option value="Mrs.">{t('Mrs')}.</Option>
                                                    <Option value="Dr.">{t('Dr')}.</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    ) : null}

                                    {fields.first_name.visible ? (
                                        <Col xs={24} md={12} xl={6}>
                                            <Form.Item
                                                name="first_name"
                                                label={fields.first_name.caption}
                                                rules={[
                                                    ...[
                                                        {
                                                            required: fields.first_name.required,
                                                            message: t('Please Enter First Name'),
                                                        },
                                                        {
                                                            pattern: /^[^\s].*$/, // new rule: first character cannot be a space
                                                            message: 'First character cannot be a space!',
                                                        },
                                                        {
                                                            max: 100,
                                                            message: t('Length cannot exceed 100 characters !'),
                                                        },
                                                    ],
                                                    // validation
                                                    ...commonInputRules,
                                                ]}
                                                validateStatus={validation && validation.first_name.validate_status}
                                                help={validation && validation.first_name.help_text}
                                                // validateStatus={!initialValues.first_name && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.first_name && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <InputComponent disabled={fields.first_name.disabled} autoComplete="off" />
                                            </Form.Item>
                                        </Col>
                                    ) : null}

                                    {fields.last_name.visible ? (
                                        <Col xs={24} md={12} xl={6}>
                                            <Form.Item
                                                name="last_name"
                                                label={fields.last_name.caption}
                                                rules={[
                                                    ...[
                                                        {
                                                            required: fields.last_name.required,
                                                            message: t('Please Enter Last Name'),
                                                        },
                                                        {
                                                            pattern: /^[^\s].*$/, //  first character cannot be a space
                                                            message: 'First character cannot be a space!',
                                                        },
                                                        {
                                                            max: 100,
                                                            message: t('Length cannot exceed 100 characters !'),
                                                        },

                                                        // {
                                                        //     pattern: /^[a-zA-Z\s]*$/g,
                                                        //     message: 'Please Enter Valid Name!',
                                                        // },
                                                        ...commonInputRules,
                                                    ],
                                                ]}
                                                validateStatus={validation && validation.last_name.validate_status}
                                                help={validation && validation.last_name.help_text}
                                                // validateStatus={!initialValues.last_name && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.last_name && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <InputComponent disabled={fields.last_name.disabled} autoComplete="off" />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                    {fields.gender.visible ? (
                                        <Col xs={24} md={12} xl={6}>
                                            <Form.Item
                                                name="gender"
                                                label={fields.gender.caption}
                                                rules={[{ required: fields.gender.required, message: t('Please Select Gender') }]}
                                                validateStatus={validation && validation.gender.validate_status}
                                                help={validation && validation.gender.help_text}
                                                // validateStatus={!initialValues.gender && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.gender && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <Select disabled={fields.gender.disabled || props.editFieldsDisable || props.gender_validation}>
                                                    <Option value="Male">{t('Male')}</Option>
                                                    <Option value="Female">{t('Female')}</Option>
                                                    <Option value="Other">{t('Other')}</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                </Row>

                                <Row gutter={20}>
                                    <div className="dob-age-container">
                                        <Col xs={24} md={24} xl={24}>
                                            {fields.date_of_birth.visible ? (
                                                <Form.Item
                                                    name="date_of_birth"
                                                    // label="DATE OF BIRTH"
                                                    label={fields.date_of_birth.caption}
                                                    rules={[{ required: true, message: t('Please Select Date of Birth ') }]}
                                                    validateStatus={validation && validation.date_of_birth.validate_status}
                                                    help={validation && validation.date_of_birth.help_text}
                                                    // validateStatus={!initialValues.date_of_birth && props.completeProfile ? 'warning' : ''}
                                                    // help={!initialValues.date_of_birth && props.completeProfile ? 'Please fill the details' : ''}
                                                >
                                                    <AgeSelection
                                                        field="date_of_birth"
                                                        form={form}
                                                        ageValues={
                                                            //In case of edit age should prepopulate
                                                            initialValues && initialValues.date_of_birth
                                                                ? {
                                                                      dob: initialValues.date_of_birth.dob
                                                                          ? moment(initialValues.date_of_birth.dob, 'YYYY-MM-DD')
                                                                          : null,
                                                                      year: initialValues.date_of_birth.year,
                                                                      month: initialValues.date_of_birth.month,
                                                                      day: initialValues.date_of_birth.day,
                                                                  }
                                                                : null
                                                        }
                                                        disabled={fields.date_of_birth.disabled}
                                                        //The Date of Birth should be Mandatory in the registration screen, even if the Age is given.

                                                        dobValidation={props.dobValidation}
                                                    />
                                                </Form.Item>
                                            ) : null}
                                        </Col>
                                    </div>
                                    {fields.alternate_mobile.visible ? (
                                        <Col xs={24} md={24} xl={12}>
                                            <Form.Item
                                                rules={[
                                                    {
                                                        required: fields.alternate_mobile.required,
                                                        message: `The input is not valid ${fields.alternate_mobile.caption}`,
                                                    },
                                                    // {
                                                    //     validator(_, value) {

                                                    //         if (value && value.value && value.code) {

                                                    //             // checking the number is valid or not
                                                    //             let numberValid = validatePhoneNumber(value.value, value.code.dialCode)

                                                    //             if (!numberValid.valid) {

                                                    //                 return Promise.reject('Please provide a valid mobile number');

                                                    //             } else {

                                                    //                 return Promise.resolve();

                                                    //             }
                                                    //         } else {
                                                    //             return Promise.resolve();
                                                    //         }

                                                    //     },
                                                    // },
                                                ]}
                                                name="alternate_mobile"
                                                label={fields.alternate_mobile.caption}
                                                className="phone-input"

                                                // validateStatus={!initialValues.alternate_mobile && params['complete-profile'] ? 'warning' : ''}
                                                //help={!initialValues.alternate_mobile && params['complete-profile'] ? 'Please fill the details' : ''}
                                            >
                                                <CountryPhoneInput
                                                    disableCountryCode={disableCountryCode}
                                                    required={fields.alternate_mobile.required}
                                                    disabled={fields.alternate_mobile.disabled}
                                                    defaultCountryCode={process.env.REACT_APP_COUNTRYCODE || 'in'}
                                                    autoComplete="off"
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}

                                    {/* Communication Mode */}
                                    {fields.mode_of_communication.visible ? (
                                        <Col xs={24} md={12} xl={12}>
                                            <Form.Item
                                                rules={[
                                                    {
                                                        required: fields.mode_of_communication.required,
                                                        message: t('The input is not valid selection!'),
                                                    },
                                                ]}
                                                name="mode_of_communication"
                                                label={fields.mode_of_communication.caption}
                                                className="phone-input"
                                                validateStatus={validation && validation.mode_of_communication.validate_status}
                                                help={validation && validation.mode_of_communication.help_text}
                                                //validateStatus={!initialValues.mode_of_communication && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.mode_of_communication && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <ReferenceSelect
                                                    // disabled={disabledfield}
                                                    label="comt_desc"
                                                    mode="nura-base"
                                                    model={Communications}
                                                    field={'comt_code'}
                                                    disabled={fields.mode_of_communication.disabled}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}

                                    {fields.category.visible ? (
                                        <Col xs={24} md={12} xl={12}>
                                            <Form.Item
                                                rules={[
                                                    {
                                                        required: fields.category.required,
                                                        message: t('Please select Category'),
                                                    },
                                                ]}
                                                name="category"
                                                label={fields.category.caption}
                                                className="phone-input"
                                                validateStatus={validation && validation.category.validate_status}
                                                help={validation && validation.category.help_text}
                                                // validateStatus={!initialValues.category && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.category && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <Select disabled={fields.category.disabled}>
                                                    <Option value="GEN">{t('General')}</Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                </Row>
                                <Row gutter={20}>
                                    {fields.national_id.visible ? (
                                        <Col xs={24} md={12} xl={8}>
                                            <Form.Item
                                                name={'national_id'}
                                                label={fields.national_id.caption}
                                                rules={[
                                                    {
                                                        required: fields.national_id.required,
                                                        message: `Please enter ${fields.national_id.caption}`,
                                                    },
                                                ]}
                                                validateStatus={validation && validation.national_id.validate_status}
                                                help={validation && validation.national_id.help_text}
                                                // validateStatus={!initialValues.national_id && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.national_id && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <ReferenceSelect
                                                    label="lcnt_nationalitydesc"
                                                    mode="nura-base"
                                                    model={ListCountries}
                                                    field={'lcnt_id'}
                                                    disabled={fields.national_id.disabled}
                                                    defaultValueCondition={identifyDefaultCountry} //Default value condition for a component, using the identifyDefaultCountry function to determine the default country.
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                    {fields.nature.visible ? (
                                        <Col xs={24} md={12} xl={8}>
                                            <Form.Item
                                                name={'nature'}
                                                label={fields.nature.caption}
                                                rules={[
                                                    {
                                                        required: fields.nature.required,
                                                        message: `Please select nature`,
                                                    },
                                                ]}
                                            >
                                                <ReferenceSelect
                                                    label="desc"
                                                    mode="nura-base"
                                                    model={NatureList}
                                                    field={'code'}
                                                    disabled={fields.nature.disabled}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                    {fields.passport.visible ? (
                                        <Col xs={24} md={10} xl={8}>
                                            <Form.Item
                                                name="passport"
                                                label={fields.passport.caption}
                                                className="phone-input"
                                                rules={[
                                                    {
                                                        required: fields.passport.required,
                                                        message: `Please enter ${fields.passport.caption}`,
                                                    },

                                                    {
                                                        max: 10,
                                                        message: t('Length cannot exceed 10 characters'),
                                                    },
                                                ]}
                                            >
                                                <InputComponent disabled={fields.passport.disabled} autoComplete="off" />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                </Row>
                                <Row gutter={20}>
                                    {fields.referral_type.visible ? (
                                        <Col xs={10} md={10} xl={8}>
                                            <Form.Item
                                                name={'referral_type'}
                                                label={fields.referral_type.caption}
                                                rules={[
                                                    {
                                                        required: fields.referral_type.required,
                                                        message: `Please enter ${fields.referral_type.caption}`,
                                                    },
                                                ]}
                                                validateStatus={validation && validation.referral_type.validate_status}
                                                help={validation && validation.referral_type.help_text}
                                                // validateStatus={!initialValues.camp_site && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.camp_site && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <ReferenceSelect
                                                    label="reft_desc"
                                                    mode="nura-base"
                                                    model={ReferenceTypes}
                                                    // onChange={(value) => handleReferralTypeChange(value)} //Capture change event
                                                    onChange={async (value) => {
                                                        let referrerLists = await initialReferrerLists();
                                                        if (filterReferrer) {
                                                            // console.log('onChange', value);
                                                            handleReferralTypeChange(value);
                                                        } else {
                                                            // handleReferralTypeChange(value);
                                                            setReferrerOptions(referrerLists);
                                                        }
                                                    }}
                                                    disabled={validation?.referral_type?.disabled || fields.referral_type.disabled}
                                                    allowClear={true}
                                                    // onClear={(value) => handleReferralTypeChange(value, mergedReferrerData)} // This will be called when the user clicks the clear icon
                                                    onClear={(value) => {
                                                        if (filterReferrer) {
                                                            handleReferralTypeChange(value, mergedReferrerData);
                                                        }
                                                    }}
                                                    config={{
                                                        queries: [
                                                            {
                                                                field: 'reft_active',

                                                                value: 'Y',
                                                            },
                                                        ],
                                                    }}
                                                    field={'reft_code'}
                                                    // defaultValueCondition={identifyDefaultReferralType}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                    {fields.limit_by_source.visible ? (
                                        <Col xs={6} md={6} xl={6}>
                                            <Form.Item
                                                rules={[
                                                    {
                                                        checked: fields.limit_by_source.required,
                                                        message: t('Enter source'),
                                                    },
                                                ]}
                                                name="limit_by_source"
                                                label={fields.limit_by_source.caption}
                                                // label="Filter by Referrer Type"
                                                className="phone-input"
                                            >
                                                <Checkbox
                                                    onChange={filterByReferrer}
                                                    checked={filterReferrer}
                                                    defaultChecked={filterReferrer}
                                                    disabled={validation?.limit_by_source?.disabled || fields.limit_by_source.disabled}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                    {fields.camp_site.visible && props.guestPreferenceDisable ? (
                                        <Col xs={10} md={10} xl={8}>
                                            <Form.Item
                                                name={'camp_site'}
                                                label={fields.camp_site.caption}
                                                rules={[
                                                    {
                                                        required: fields.camp_site.required,
                                                        message: `Please enter ${fields.camp_site.caption}`,
                                                    },
                                                ]}
                                                validateStatus={validation && validation.camp_site.validate_status}
                                                help={validation && validation.camp_site.help_text}
                                                // validateStatus={!initialValues.camp_site && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.camp_site && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <ReferenceSelect
                                                    label="refr_desc"
                                                    mode="nura-base"
                                                    model={Referrers}
                                                    allowClear={true}
                                                    //  already have a referrer need to prepopulate referrer set disable.
                                                    disabled={validation?.camp_site?.disabled || fields.camp_site.disabled}
                                                    // config={{
                                                    //     queries: [
                                                    //         {
                                                    //             field: 'refr_active',

                                                    //             value: 'Y',
                                                    //         },
                                                    //     ],
                                                    // }}
                                                    // onChange={(value) => {
                                                    //     if (!filterReferrer) {
                                                    //         findAndHandleReferralTypeChange(mergedReferrerData, value);
                                                    //     }
                                                    // }}
                                                    options={referrerOptions.map((item) => ({
                                                        label: item.refr_desc, // Display referrer description
                                                        value: item.refr_code, // Use referrer code as value
                                                    }))}
                                                    filterOption={(inputValue, option) => {
                                                        // Filter by refr_desc based on the user input value
                                                        return option?.label?.toLowerCase().includes(inputValue?.toLowerCase());
                                                    }}
                                                    field={'refr_code'}
                                                    defaultValueCondition={identifyDefaultReferrer}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                </Row>

                                {/**guestPreferenceDisableis the attribute used to check if the case is of nura or stoptb
                                 * Here is guestPreferenceDisable is true, campsite is shown as a part of basic information
                                 */}
                                <Row gutter={20}>
                                    {fields.othernatid.visible ? (
                                        <Col xs={24} md={10} xl={8}>
                                            <Form.Item
                                                name="othernatid"
                                                label={fields.othernatid.caption}
                                                className="phone-input"
                                                rules={((field) => {
                                                    let rules = [
                                                        {
                                                            required: field.required,
                                                            message: `Please enter ${field.caption}`,
                                                        },
                                                    ];

                                                    if (field.pattern) {
                                                        try {
                                                            rules.push({
                                                                pattern: new RegExp(field.pattern),
                                                                message: t('The value is not in a valid format.'),
                                                            });
                                                        } catch (e) {
                                                            console.error('Invalid regex pattern');
                                                        }
                                                    } else {
                                                        rules.push({
                                                            min: 15,
                                                            max: 15,
                                                            message: t('The value must be exactly 15 characters'),
                                                        });
                                                    }

                                                    return rules;
                                                })(fields.othernatid)}
                                                validateStatus={validation && validation.othernatid.validate_status}
                                                help={validation && validation.othernatid.help_text}
                                            >
                                                <Input autoComplete="off" disabled={props.emiratesCardDisable || fields.othernatid.disabled} />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                    {fields.religion.visible ? (
                                        <Col xs={24} md={12} xl={8}>
                                            <Form.Item
                                                name={'religion'}
                                                label={fields.religion.caption}
                                                rules={[
                                                    {
                                                        required: fields.religion.required,
                                                        message: `Please enter ${fields.religion.caption}`,
                                                    },
                                                ]}
                                                validateStatus={validation && validation.religion.validate_status}
                                                help={validation && validation.religion.help_text}
                                            >
                                                <ReferenceSelect
                                                    config={{
                                                        queries: [
                                                            {
                                                                field: 'genl_mode',
                                                                value: 'RLGN',
                                                            },
                                                        ],
                                                    }}
                                                    label="genl_desc"
                                                    mode="nura-base"
                                                    disabled={fields.religion.disabled}
                                                    model={GeneralLists}
                                                    field={'genl_code'}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                    {fields.marital.visible ? (
                                        <Col xs={24} md={12} xl={8}>
                                            <Form.Item
                                                name={'marital'}
                                                label={fields.marital.caption}
                                                rules={[
                                                    {
                                                        required: fields.marital.required,
                                                        message: `Please enter ${fields.marital.caption}`,
                                                    },
                                                ]}
                                                validateStatus={validation && validation.marital.validate_status}
                                                help={validation && validation.marital.help_text}
                                            >
                                                <ReferenceSelect
                                                    label="mrts_desc"
                                                    mode="nura-base"
                                                    model={Maritalstatus}
                                                    disabled={fields.marital.disabled}
                                                    field={'mrts_desc'}
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}

                                    {fields.remarks.visible ? (
                                        <Col xs={24} md={10} xl={10}>
                                            <Form.Item
                                                rules={[
                                                    ...(fields.remarks.required
                                                        ? [
                                                              {
                                                                  required: true,
                                                                  validator: (_, value) => {
                                                                      if (!value || value.trim().length === 0) {
                                                                          return Promise.reject(new Error('Enter Remarks'));
                                                                      }
                                                                      return Promise.resolve();
                                                                  },
                                                              },
                                                          ]
                                                        : []),
                                                    {
                                                        max: 250,
                                                        message: 'Length cannot exceed 250 characters !',
                                                    },
                                                ]}
                                                validateStatus={validation && validation.remarks.validate_status}
                                                help={validation && validation.remarks.help_text}
                                                name="remarks"
                                                label={fields.remarks.caption}
                                                className="phone-input"
                                            >
                                                <InputComponent disabled={isRemarksDisabled} autoComplete="off" />
                                            </Form.Item>
                                        </Col>
                                    ) : null}
                                </Row>
                                <Row gutter={20}>
                                    {fields.corporate.visible ? (
                                        <Col xs={24} md={6} xl={6}>
                                            <Form.Item
                                                rules={
                                                    !!user?.corporate
                                                        ? []
                                                        : [
                                                              {
                                                                  required: fields.corporate.required,
                                                                  message: t('Enter Corporate'),
                                                              },
                                                          ]
                                                }
                                                name="corporate"
                                                label={fields.corporate.caption}
                                                className="phone-input"
                                                validateStatus={validation && validation.corporate.validate_status}
                                                help={validation && validation.corporate.help_text}
                                                // /validateStatus={!initialValues.corporate && props.completeProfile ? 'warning' : ''}
                                                // help={!initialValues.corporate && props.completeProfile ? 'Please fill the details' : ''}
                                            >
                                                <Checkbox
                                                    onChange={callBack}
                                                    checked={corporate}
                                                    defaultChecked={corporate}
                                                    /**If corporateValue,coupon having a corporate mapped or fields.corporate contains disabled then the check box will be disabled */
                                                    disabled={
                                                        !!user?.corporate || //  readonly if user already has corporate
                                                        (initialValues?.coupon_corporate_referrer_details?.corporate_pointer ||
                                                        fields.corporate_pointer.corporate_disable
                                                            ? true
                                                            : false) ||
                                                        (corporateValue ? props.disableCorporate : false) ||
                                                        fields.corporate.disabled
                                                    }
                                                />
                                            </Form.Item>
                                        </Col>
                                    ) : null}

                                    {/* 
                                    Only if the is corporate is true , we 
                                    have to show the below block
                                    */}
                                    {corporate ? (
                                        <>
                                            {fields.corporate_pointer.visible ? (
                                                <Col xs={24} md={12} xl={8}>
                                                    <Form.Item
                                                        rules={[
                                                            {
                                                                required: fields.corporate_pointer.required,
                                                                message: `Enter ${fields.corporate_pointer.caption}`,
                                                            },
                                                        ]}
                                                        name="corporate_pointer"
                                                        label={fields.corporate_pointer.caption}
                                                        className="phone-input"
                                                        validateStatus={validation && validation.corporate_pointer.validate_status}
                                                        help={validation && validation.corporate_pointer.help_text}
                                                        // validateStatus={
                                                        //     !initialValues.corporate_pointer && params['complete-profile'] ? 'warning' : ''
                                                        // }
                                                        // help={
                                                        //     !initialValues.corporate_pointer && params['complete-profile']
                                                        //         ? 'Please fill the details'
                                                        //         : ''
                                                        // }
                                                    >
                                                        <ReferenceSelect
                                                            // onChange={onCorperateChange}
                                                            config={{
                                                                queries: [
                                                                    {
                                                                        field: 'cc_active',

                                                                        value: 'Y',
                                                                    },
                                                                ],
                                                            }}
                                                            options={corporateOptions}
                                                            // Passes 'true' on onSelectChange when selecting a corporate with referrer → Starts loading.
                                                            onChange={(value, record) => {
                                                                onSelectChange(value, true);
                                                                setSelectCorporate(record?.record);
                                                            }}
                                                            label="cc_desc"
                                                            mode="nura-base"
                                                            allowClear={true}
                                                            model={Customers}
                                                            field={'cc_code'}
                                                            // if coupon having a corporate mapped or corporate value is there and props having disableCorporate true then disable corporate
                                                            disabled={
                                                                corporateDisabled ||
                                                                (initialValues?.coupon_corporate_referrer_details?.corporate_pointer ||
                                                                fields.corporate_pointer.corporate_disable
                                                                    ? true
                                                                    : false) ||
                                                                (corporateValue ? props.disableCorporate : false) ||
                                                                fields.corporate_pointer.disabled
                                                            } // This will disable corporate
                                                        />
                                                    </Form.Item>

                                                    {selectCorporate?.cc_styledcontent?.trim() && (
                                                        <Button size={'medium'} onClick={showOffersModal}>
                                                            {t('View offers')}
                                                        </Button>
                                                    )}
                                                    <Modal visible={isModalVisible} onCancel={closeOffersModal} footer={null}>
                                                        {selectCorporate?.cc_styledcontent?.trim() ? (
                                                            <div dangerouslySetInnerHTML={{ __html: selectCorporate.cc_styledcontent }}></div>
                                                        ) : (
                                                            <p>{t('No content available')}</p>
                                                        )}
                                                    </Modal>
                                                </Col>
                                            ) : null}

                                            {fields.corporate_information.visible ? (
                                                <Col xs={24} md={12} xl={8}>
                                                    <Form.Item
                                                        rules={[
                                                            {
                                                                required: fields.corporate_information.required,
                                                                message: `Enter ${fields.corporate_information.caption}`,
                                                            },

                                                            {
                                                                max: 50,
                                                                message: 'Length cannot exceed 50 characters !',
                                                            },
                                                        ]}
                                                        name="corporate_information"
                                                        label={fields.corporate_information.caption}
                                                        className="phone-input"
                                                        validateStatus={validation && validation.corporate_information.validate_status}
                                                        help={validation && validation.corporate_information.help_text}
                                                        // validateStatus={
                                                        //     !initialValues.corporate_information && props.completeProfile ? 'warning' : ''
                                                        // }
                                                        // help={
                                                        //     !initialValues.corporate_information && props.completeProfile
                                                        //         ? 'Please fill the details'
                                                        //         : ''
                                                        // }
                                                    >
                                                        {/* disable corporate value  */}
                                                        <InputComponent
                                                            disabled={
                                                                (initialValues?.coupon_corporate_referrer_details?.corporate_pointer
                                                                    ? true
                                                                    : false) ||
                                                                (corporateValue ? props.disableCorporate : false) ||
                                                                fields.corporate_information.disabled
                                                            }
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            ) : null}
                                        </>
                                    ) : null}
                                </Row>

                                {props.disable_cluster ? null : (
                                    <>
                                        {/* Communication Mode */}
                                        <Row gutter={20}>
                                            <AdditionalKeyGenerate
                                                seperator={props.seperator}
                                                form={form}
                                                callback={(values) => (additionalkey = values)}
                                                // In case of edit cluster_number and serial_number needs to be prepopulated
                                                additionalKeyValues={
                                                    initialValues && initialValues.cluster_number
                                                        ? {
                                                              cluster_number: initialValues.cluster_number,
                                                              serial_number: initialValues.serial_number,
                                                          }
                                                        : null
                                                }
                                                //Only in case of edit cluster field should be readonly
                                                edit={params.edit ? true : false}
                                            ></AdditionalKeyGenerate>
                                        </Row>
                                    </>
                                )}

                                {/* Communication Mode Ends */}
                            </div>
                            {/** Form content  of patient section Ends */}

                            {/** other details of patient section start */}

                            <div className="other-details ">
                                <div className="section-2">
                                    <Tabs defaultActiveKey="0">
                                        {/** Tab used for address details of guest start */}
                                        <TabPane tab={t('ADDRESS DETAILS')} key="0">
                                            <AddressDetails config={config} props={props} initialValues={initialValues} validation={validation} />
                                        </TabPane>

                                        {/** Tab used for address details of guest end */}

                                        {/** Tab used for Registration details of guest start */}

                                        {/* <TabPane tab="REGISTRATION DETAILS" key="1">
                                                <GuestRegistrationAddressDetails config={config} props={props} />
                                            </TabPane> */}
                                        {/** Tab used for Registration details of guest end */}

                                        {/** Tab used for personal details of guest start */}

                                        {/* <TabPane tab="PERSONAL DETAILS" key="2">
                                                <PersonalDetails config={config} />
                                            </TabPane> */}
                                        {/** Tab used for address details of guest ends */}

                                        {/** Tab used for Campaign details of guest start */}

                                        {/* <TabPane tab="CAMPAIGN DETAILS" key="3"> */}
                                        {/**guestPreferenceDisableis the attribute used to check if the case is of nura or stoptb
                                         * Here is guestPreferenceDisable is true, campsite is shown as a part of basic information
                                         * Here it is disabled
                                         */}
                                        {/* {props.guestPreferenceDisable ? null : <CampaignDetails config={config} />} */}
                                        {/* </TabPane> */}
                                        {/** Tab used for Other details of guest start */}
                                        {props.otherdetails_disable ? null : (
                                            <TabPane tab="OTHER DETAILS" key="4">
                                                <OtherDetailsOfGuest config={config} />
                                            </TabPane>
                                        )}
                                        {/** Tab used for other details of guest end */}
                                    </Tabs>
                                </div>
                            </div>
                            {/** other details of patient section ends */}
                        </div>
                    </div>

                    <div className="footer-actions">
                        <div className="actions">
                            <Button type="primary" htmlType="submit" loading={btnloading}>
                                {t('Submit')}
                            </Button>

                            {/* <Button htmlType="submit">
                                        Cancel
                                    </Button> */}
                        </div>
                    </div>
                </Form>
                {/**Modal component to handle the scenario where a user already exists and The settig is 'NAP' */}
                <Modal
                    maskClosable={false}
                    destroyOnClose={true} // Ensure the modal is destroyed on close to reset its state
                    title="" // No title for the modal
                    visible={alreadyExist} // Control the visibility of the modal
                    okText="Yes" // Text for the OK button
                    cancelText="NO" // Text for the Cancel button
                    closable={false}
                    onOk={() => {
                        // Callback for when the OK button is clicked
                        onFinish({ verified: true }); // Execute onFinish with verified parameter
                        setBtnLoading(false); // Stop any button loading state
                        setAlreadyExist(false); // Hide the modal
                    }}
                    onCancel={() => {
                        // Callback for when the Cancel button is clicked
                        setBtnLoading(false); // Stop any button loading state
                        setAlreadyExist(false); // Hide the modal

                        onFinish({ verified: true, existing_user: true }); // Execute onFinish with specific parameters
                    }}
                >
                    <p>There is an account already registered with given details. Are you taking appointment for the same guest ?</p>
                </Modal>

                {/** if there is a appoinment with same email and name*/}
                <Modal
                    destroyOnClose={true}
                    title=""
                    visible={alreadyBooked}
                    cancelButtonProps={{
                        style: {
                            display: mandatorySetting ? 'none' : 'true',
                        },
                    }}
                    okText="Okay"
                    onOk={() => {
                        onFinish({ verified: true });
                        setBtnLoading(false);
                        setAlreadyBooked(false);
                    }}
                    onCancel={() => {
                        setBtnLoading(false);
                        setAlreadyBooked(false);
                    }}
                >
                    {mandatorySetting ? (
                        <p>{t('You already have an Appointment booked So you cant take an Appointment')}</p>
                    ) : (
                        <p>{t('You already have an Appointment for this Do you wish to proceed?')}</p>
                    )}
                </Modal>
                {/** Modal to list existing customer with same mail address starts */}
                <Modal
                    destroyOnClose={true}
                    footer={null}
                    title={t('Account Selection')}
                    visible={existingCustomerVisible}
                    width={'90%'}
                    okText="Okay"
                    cancelText="Cancel"
                    onOk={() => {
                        setExistingCustomerVisible(false);
                    }}
                    onCancel={() => {
                        setExistingCustomerVisible(false);
                    }}
                >
                    <ExistingCustomerList
                        selectedCustomer={selectedCustomer}
                        existingCustomers={existingCustomers}
                        searchBy={searchBy}
                        searchText={searchText}
                        props={props}
                        callBack={(element) => {
                            setSelectedCustomer(element);
                        }}
                        setVisible={() => setExistingCustomerVisible(false)}
                    />
                </Modal>
                {/** Modal to list existing customer with same mail address ends */}
                {/** Modal for change address alert */}
                <Modal
                    destroyOnClose={true}
                    open={changeAddressAlert}
                    width={'50%'}
                    okText="Yes"
                    cancelText="No"
                    onOk={() => {
                        const key = {
                            pincode: form.getFieldValue('pincode'),
                        };
                        const confirmed = true;
                        onValuesChange(key, form.getFieldsValue(), confirmed);
                        setChangeAddressAlert(false);
                    }}
                    onCancel={() => {
                        setChangeAddressAlert(false);
                    }}
                >
                    <p>{t('Do you wish to change the address with respect to the pincode entered?')}</p>
                    {addressChangedFields.map((ele) => {
                        return (
                            <p>
                                {ele.field} : {ele.previousValue} to {ele.newvalue}
                            </p>
                        );
                    })}
                </Modal>
                {/**  Modal for change address alert */}
            </Card>
            {/** GuestPreferences card section start */}
            <div className="primary-details">
                {/* Guest Preference */}
                {props.guestPreferenceDisable ? null : <GuestPreferences />}
                {/* Guest Preference Ends */}

                {!props.emiratesCardDisable ? null : <ReadEmiratesId loadGuestInformation={loadGuestInformation} />}
            </div>

            {/** GuestPreferences card section ends  */}
        </div>
    );
}
